import { z } from 'zod';
import { EmploymentStatusSchema } from './employee.schema.js';

/* ===========================================================================
 * THE ROSTER'S EMPLOYMENT-STATUS NARROWINGS — one shape, two homes.
 *
 * Spread into BOTH `EmployeeListQuerySchema` (a query string) and
 * `WorkerSelectionFilterSchema` (a JSON body), because a filter the list
 * applies and "select all matching" cannot express is a bulk action that
 * reaches people the reader never saw (the note on WorkerSelectionFilterSchema).
 * So every field parses from either transport:
 *
 *   employmentStatuses  Hadir statuses, any of — an array, or "A,B" in a URL.
 *   statusCode          the ERP's own code, exactly as the sync stored it.
 *   statusSetByHr       true: only people whose status HR set by hand
 *                       (`employmentStatusManualAt`); false: only those it did not.
 *   statusWithoutCode   true: only people whose status is Hadir's own — no ERP
 *                       code on file, or HR's choice over it. With
 *                       `employmentStatuses` it is the Status control's
 *                       "Active · no ERP code"; `status=ACTIVE` alone still
 *                       means everyone ACTIVE, as old links expect.
 *   onRoll              true: only people on the roll — the Overview's own
 *                       rule (`ON_THE_ROLL`, api services/onTheRoll.ts), so
 *                       the roster's default headcount is the Overview's.
 * ======================================================================== */

const csvOrArray = (v: unknown): unknown =>
  typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter((s) => s !== '') : v;

const boolParam = (v: unknown): unknown => (v === 'true' ? true : v === 'false' ? false : v);

export const ErpStatusFilterShape = {
  employmentStatuses: z.preprocess(csvOrArray, z.array(EmploymentStatusSchema).max(10)).optional(),
  statusCode: z.string().trim().min(1).max(20).optional(),
  statusSetByHr: z.preprocess(boolParam, z.boolean()).optional(),
  statusWithoutCode: z.preprocess(boolParam, z.boolean()).optional(),
  onRoll: z.preprocess(boolParam, z.boolean()).optional(),
};

/** One ERP status code on file, with the words the ERP gives it. */
export interface ErpStatusCodeFacet {
  code: string;
  /** The ERP's description; `null` when no synced row carries one. */
  description: string | null;
  count: number;
  /**
   * Whether the ERP's rule puts a holder of this code ON THE ROLL (its mapped
   * Hadir status is ACTIVE / PROBATION / NOTICE_PERIOD — `onTheRoll.ts`). The
   * roster's default Status is named by these codes (owner, 2026-11-02:
   * "Only ERP statuses").
   */
  onRoll: boolean;
}

/**
 * A Hadir status some people in scope hold WITHOUT an ERP code describing it —
 * no code on file, or HR set the status by hand. The roster's one Status
 * control offers these after the codes, so nobody is unreachable by it.
 * `count` is only those people — what `statusWithoutCode` plus the status
 * lands on — never everyone holding the status, whom the codes already count.
 */
export interface UncodedStatusFacet {
  status: z.infer<typeof EmploymentStatusSchema>;
  count: number;
}

/** One value a roster filter can offer, and how many people in scope hold it. */
export interface RosterFacetOption {
  /** What the filter sends: an id, or a nationality exactly as stored. */
  value: string;
  label: string;
  count: number;
}

/** The roster filter's option lists, read off the people in the caller's scope. */
export interface RosterFilterFacets {
  department: RosterFacetOption[];
  designation: RosterFacetOption[];
  camp: RosterFacetOption[];
  branch: RosterFacetOption[];
  supplier: RosterFacetOption[];
  nationality: RosterFacetOption[];
  /** ERP payroll codes people hold (`erpPayrollNumber`), labelled by the
   *  connection's payroll names. Optional: an older server omits it. */
  payroll?: RosterFacetOption[];
}
